// ProjectMapPage.jsx — World map + satellite grid for BRIO locations
// Globe: Canvas + d3-geo (single rAF draw pass, zero React state in render loop)
// Satellite: Google Maps JS API (env: REACT_APP_GOOGLE_MAPS_KEY)
//
// Three independently toggled layers, each with its own marker colour:
//   - Projects:  project addresses (on by default)
//   - Inquiries: inquiry locations
//   - Team:      team members' home towns, scoped to active / inactive / all
// Any combination can be shown, including team members with projects off.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../supabaseClient';


// ========================================================================
// CONSTANTS
// ========================================================================
const BRIO_GOLD_HEX = '#9F902A';
const LAND_COLOR = '#bcb16a';
const LAND_HOVER = '#d0c67e';
const LAND_STROKE = '#ffffff';
const MARKER_COLOR = '#222222';
// BRIO amber (--brio-orange). Gold reads as the olive land underneath; amber
// stays legible against it and against the other two marker colours.
const INQUIRY_MARKER_COLOR = '#DB6B5D';
const TEAM_MARKER_COLOR = '#2E6F8E';
const GRATICULE_STROKE = '#ffffff';
const SPHERE_STROKE = '#ffffff';
const NOMINATIM_DELAY_MS = 1100;

// Satellite zoom per point precision: an exact site address deserves a
// building-level view, a city name only makes sense zoomed out.
const ZOOM_ADDRESS = 18;
const ZOOM_COORDINATES = 17;
const ZOOM_CITY = 12;
const ZOOM_HOME_TOWN = 11;

const TEAM_SCOPE_ACTIVE = 'active';
const TEAM_SCOPE_INACTIVE = 'inactive';
const TEAM_SCOPE_ALL = 'all';

const TEAM_SCOPE_LABELS = {
  [TEAM_SCOPE_ACTIVE]: 'Active',
  [TEAM_SCOPE_INACTIVE]: 'Inactive',
  [TEAM_SCOPE_ALL]: 'All',
};

// One text-keyed geocode cache for every layer, so the same place — a project
// address, an inquiry city, a home town — is only ever looked up once, and an
// edited location keys to a fresh lookup rather than a stale cached pin.
// (The older id-keyed project_geocodes table is no longer read or written: it
// keyed coordinates by project id, so an edited address kept its old pin.)
const LOCATION_GEOCODE_TABLE = 'location_geocodes';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

const TOPO_URL = '/maps/countries-50m.json';

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const BASE_SCALE = 220;
const MAP_WIDTH = 800;
const MAP_HEIGHT = 500;

// Initial view: India centered
const INITIAL_ROTATION = [78, 22];

const DEV_MODE = process.env.NODE_ENV === 'development';

const SATELLITE_HIDE_LABELS_STYLE = [
  { elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

// ========================================================================
// ANTARCTICA FILTER
// ========================================================================
function isAntarctica(geo) {
  const id = geo.id || geo.properties?.id;
  const name = (geo.properties?.name || '').toLowerCase();
  const iso = (geo.properties?.ISO_A3 || geo.properties?.iso_a3 || '').toUpperCase();
  if (id === '010' || id === 10) return true;
  if (iso === 'ATA') return true;
  if (name === 'antarctica') return true;
  return false;
}

// ------------------------------------------------------------------------
// GEOCODING HOOK
// ------------------------------------------------------------------------
// Takes raw points ({ key, cacheKey, name, subtitle, address, lat, lng, ... })
// and returns the ones that have coordinates, geocoding whatever is missing.
// Points that already carry lat/lng render immediately; the rest stream in as
// they resolve. Identical addresses are looked up once and shared.
function useGeocodedPoints(points, { enabled = true, cache = null } = {}) {
  const [resolved, setResolved] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const pointsRef = useRef(points);
  pointsRef.current = points;

  const signature = useMemo(() => (points || [])
    .map(p => `${p.key}~${p.cacheKey ?? ''}~${p.address || ''}~${p.lat ?? ''}~${p.lng ?? ''}`)
    .join('|'), [points]);

  useEffect(() => {
    const all = pointsRef.current || [];
    if (!enabled || !all.length) {
      setResolved([]);
      setGeocoding(false);
      setProgress({ done: 0, total: 0 });
      return undefined;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    const ready = all.filter(p => isValidLatLng(p.lat, p.lng));
    setResolved(ready);

    // Group the un-geocoded points by cache key so one lookup serves every
    // point sharing that address.
    const pending = new Map();
    all.forEach(p => {
      if (isValidLatLng(p.lat, p.lng)) return;
      if (!p.address || !p.address.trim()) return;
      const key = String(p.cacheKey ?? normalizeQuery(p.address));
      if (!pending.has(key)) pending.set(key, { rawKey: p.cacheKey ?? key, address: p.address, points: [] });
      pending.get(key).points.push(p);
    });

    if (!pending.size) {
      setGeocoding(false);
      setProgress({ done: 0, total: 0 });
      return undefined;
    }

    async function run() {
      setGeocoding(true);
      setProgress({ done: 0, total: pending.size });

      const cached = cache ? await cache.read([...pending.values()].map(e => e.rawKey)) : new Map();
      if (cancelled) return;

      const found = [...ready];
      let done = 0;

      for (const [key, entry] of pending) {
        if (cancelled) return;

        let coords;
        if (cached.has(key)) {
          coords = cached.get(key);
          _sessionGeocodes.set(key, coords);
        } else if (_sessionGeocodes.has(key)) {
          coords = _sessionGeocodes.get(key);
        } else {
          coords = await geocodeWithRetry(entry.address, isCancelled, entry.points[0]?.name);
          if (cancelled) return;
          _sessionGeocodes.set(key, coords);
          if (cache) await cache.write(entry.rawKey, coords);
          if (!coords) console.warn(`No coordinates found for "${entry.address}"`);
        }

        if (coords && isValidLatLng(coords.lat, coords.lng)) {
          entry.points.forEach(p => found.push({ ...p, lat: coords.lat, lng: coords.lng }));
          if (!cancelled) setResolved([...found]);
        }

        done++;
        if (!cancelled) setProgress({ done, total: pending.size });
      }

      if (!cancelled) {
        setResolved(found);
        setGeocoding(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [signature, enabled, cache]); // eslint-disable-line react-hooks/exhaustive-deps

  return { points: resolved, geocoding, progress };
}

// ========================================================================
// GOOGLE MAPS SCRIPT LOADER
// ========================================================================
let _gmapsPromise = null;
let _gmapsAuthFailed = false;
const _authFailureListeners = new Set();

// Google Maps calls this global when the API key is invalid / billing disabled
if (typeof window !== 'undefined' && !window.gm_authFailure) {
  window.gm_authFailure = () => {
    _gmapsAuthFailed = true;
    _gmapsPromise = null; // allow retry after key is fixed
    _authFailureListeners.forEach(fn => fn());
  };
}

function loadGoogleMapsScript() {
  if (_gmapsAuthFailed) {
    return Promise.reject(new Error('Google Maps authentication failed — check API key and billing'));
  }
  if (_gmapsPromise) return _gmapsPromise;
  if (window.google?.maps?.Map) {
    _gmapsPromise = Promise.resolve(window.google.maps);
    return _gmapsPromise;
  }
  if (!GOOGLE_MAPS_KEY) {
    return Promise.reject(new Error('No Google Maps API key'));
  }
  _gmapsPromise = new Promise((resolve, reject) => {
    // With loading=async, script.onload fires once the small bootstrap is
    // parsed — at which point window.google.maps exists but google.maps.Map
    // is not yet defined. Resolving here would let concurrent consumers
    // call `new maps.Map(...)` against an undefined constructor, racing the
    // lazy library load. Poll for Map (matching the existing-script branch
    // below) so every consumer sees a fully-loaded API.
    const waitForMap = () => {
      if (_gmapsAuthFailed) { reject(new Error('Google Maps authentication failed')); return; }
      if (window.google?.maps?.Map) { resolve(window.google.maps); return; }
      setTimeout(waitForMap, 50);
    };

    if (document.querySelector('script[src*="maps.googleapis.com/maps/api"]')) {
      waitForMap();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = waitForMap;
    script.onerror = () => reject(new Error('Google Maps script failed to load'));
    document.head.appendChild(script);
  });
  return _gmapsPromise;
}

// ========================================================================
// GEOCODING HELPERS
// ========================================================================

async function geocodeAddress(address) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`;
  const res = await fetch(url, {
    headers: { 'User-Agent': 'BRIOTimeTracker/1.0' }
  });
  if (res.status === 429) throw new Error('RATE_LIMIT');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data && data.length > 0) {
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  }
  return null;
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Nominatim allows one request per second per client. Several layers can be
// geocoding at the same time (sites + team overlay), so every request goes
// through one global chain that spaces calls out — no layer can outrun it.
let _geocodeChain = Promise.resolve();

export function queueGeocode(address) {
  let startedAt = 0;
  const result = _geocodeChain.then(() => {
    startedAt = Date.now();
    return geocodeAddress(address);
  });
  // Space request *starts* one interval apart rather than waiting a full
  // interval after each response — otherwise every lookup costs its own
  // round-trip on top of the delay.
  //
  // Swallow the rejection on the chain itself (the caller still sees it via
  // `result`), otherwise one failure would poison every queued lookup.
  _geocodeChain = result.catch(() => {}).then(() => {
    const elapsed = Date.now() - startedAt;
    return elapsed < NOMINATIM_DELAY_MS ? sleep(NOMINATIM_DELAY_MS - elapsed) : undefined;
  });
  return result;
}

async function geocodeWithRetry(address, isCancelled, label) {
  for (let attempt = 0; attempt < 3; attempt++) {
    if (isCancelled()) return null;
    try {
      return await queueGeocode(address);
    } catch (err) {
      if (err.message === 'RATE_LIMIT') {
        await sleep(2000 * (attempt + 1));
        continue;
      }
      console.warn(`Geocode failed for "${label || address}":`, err.message);
      return null;
    }
  }
  return null;
}

const isValidLatLng = (lat, lng) => (
  Number.isFinite(lat) && Number.isFinite(lng) &&
  Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
  !(lat === 0 && lng === 0)
);

// Inquiry coordinates are hand-entered free text: decimal pairs, DMS pairs, or
// a pasted Google Maps URL. Pull real coordinates out where we can; anything
// unparseable (short goo.gl links, other sites) falls back to the city name.
export function parseCoordinates(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text) return null;

  // Google Maps place URLs: !3d<lat>!4d<lng> is the resolved place, @lat,lng
  // is only the viewport centre — prefer the former.
  const place = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (place) {
    const lat = parseFloat(place[1]);
    const lng = parseFloat(place[2]);
    if (isValidLatLng(lat, lng)) return { lat, lng };
  }
  const viewport = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (viewport) {
    const lat = parseFloat(viewport[1]);
    const lng = parseFloat(viewport[2]);
    if (isValidLatLng(lat, lng)) return { lat, lng };
  }

  // DMS: 18°38'11.1"N 73°18'21.4"E (seconds and the direction letter optional)
  const dms = text.match(
    /(\d+)°\s*(\d+)'\s*(?:([\d.]+)")?\s*([NS])[,\s]+(\d+)°\s*(\d+)'\s*(?:([\d.]+)")?\s*([EW])/i
  );
  if (dms) {
    const toDecimal = (d, m, s, hemi) => {
      const value = Number(d) + Number(m) / 60 + (s ? Number(s) : 0) / 3600;
      return /[SW]/i.test(hemi) ? -value : value;
    };
    const lat = toDecimal(dms[1], dms[2], dms[3], dms[4]);
    const lng = toDecimal(dms[5], dms[6], dms[7], dms[8]);
    if (isValidLatLng(lat, lng)) return { lat, lng };
  }

  // Plain decimal pair — only when the value isn't a URL, where bare numbers
  // (zoom levels, ids) would match by accident.
  if (!/https?:\/\//i.test(text)) {
    const pair = text.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
    if (pair) {
      const lat = parseFloat(pair[1]);
      const lng = parseFloat(pair[2]);
      if (isValidLatLng(lat, lng)) return { lat, lng };
    }
  }

  return null;
}

// Cache key for text-geocoded locations — same city, same row.
const normalizeQuery = (address) => (address || '').trim().replace(/\s+/g, ' ').toLowerCase();

// ------------------------------------------------------------------------
// GEOCODE CACHE
// ------------------------------------------------------------------------
// Every layer caches against the location text it was geocoded from, never
// against the record's id. That makes the cache self-invalidating: correct a
// project's address or an inquiry's city and the new text is a different key,
// so the map re-geocodes instead of showing the old pin forever.
//
// read(keys) -> Map<key, {lat, lng} | null>, write(key, coords).
// A null value means "looked up before, no result" so we don't retry it every
// time the page opens. Cache failures are non-fatal — geocoding still works,
// it is just slower.

// In-memory cache for the current session, in front of the table-backed one.
// Keeps layer/scope switching instant, and keeps the page usable if the cache
// table is unavailable.
const _sessionGeocodes = new Map();

const locationGeocodeCache = {
  async read(keys) {
    const out = new Map();
    try {
      const { data, error } = await supabase
        .from(LOCATION_GEOCODE_TABLE)
        .select('query, lat, lng, resolved')
        .in('query', keys);
      if (error) throw error;
      (data || []).forEach(r => {
        out.set(r.query, r.resolved && r.lat != null && r.lng != null
          ? { lat: r.lat, lng: r.lng }
          : null);
      });
    } catch (err) {
      console.warn(`Could not read ${LOCATION_GEOCODE_TABLE} cache:`, err.message || err);
    }
    return out;
  },
  async write(key, coords) {
    try {
      await supabase.from(LOCATION_GEOCODE_TABLE).upsert({
        query: key,
        lat: coords ? coords.lat : null,
        lng: coords ? coords.lng : null,
        resolved: !!coords,
        updated_at: new Date().toISOString()
      }, { onConflict: 'query' });
    } catch (err) {
      console.warn('Failed to cache location geocode:', err.message || err);
    }
  }
};

// ========================================================================
// GOOGLE MAPS SATELLITE COMPONENT
// ========================================================================

// onTilesLoaded (optional) fires once the map has actually painted. Callers
// that need to know the map came up — rather than sitting behind Google's own
// "something went wrong" card, which no callback of ours reports — can time out
// on it and show something else.
export function GoogleSatelliteMap({ lat, lng, zoom = ZOOM_ADDRESS, interactive = false, style = {}, onTilesLoaded }) {
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null); // eslint-disable-line no-unused-vars
  const [authError, setAuthError] = useState(false);
  // Held in a ref so a caller's inline callback doesn't re-create the map.
  const onTilesLoadedRef = useRef(onTilesLoaded);
  useEffect(() => { onTilesLoadedRef.current = onTilesLoaded; });

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    // Listen for auth failures that happen after map is created
    const onAuthFail = () => { if (!cancelled) setAuthError(true); };
    _authFailureListeners.add(onAuthFail);

    // If already failed, show fallback immediately
    if (_gmapsAuthFailed) { setAuthError(true); return () => { cancelled = true; _authFailureListeners.delete(onAuthFail); }; }

    loadGoogleMapsScript()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: { lat, lng },
          zoom,
          mapTypeId: 'satellite',
          disableDefaultUI: true,
          keyboardShortcuts: false,
          clickableIcons: false,
          gestureHandling: interactive ? 'auto' : 'none',
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          zoomControl: false,
          scaleControl: false,
          rotateControl: false,
          styles: SATELLITE_HIDE_LABELS_STYLE,
        });
        mapInstanceRef.current = map;
        maps.event.addListenerOnce(map, 'tilesloaded', () => {
          if (!cancelled) onTilesLoadedRef.current?.();
        });
      })
      .catch((err) => {
        console.warn('Google Maps load failed:', err.message);
        if (!cancelled) setAuthError(true);
      });
    return () => { cancelled = true; _authFailureListeners.delete(onAuthFail); };
  }, [lat, lng, zoom, interactive]);

  if (authError) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f0', ...style }}>
        <div style={{ textAlign: 'center', padding: 16 }}>
          <p style={{ fontSize: 12, color: '#666', margin: 0 }}>Satellite view unavailable</p>
          <p style={{ fontSize: 11, color: '#999', margin: '4px 0 0' }}>Google Maps API key is invalid or billing is not enabled.</p>
        </div>
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: '100%', height: '100%', ...style }} />;
}

// ========================================================================
// SUB-COMPONENTS
// ========================================================================

function SatelliteModal({ point, onClose }) {
  if (!point) return null;
  const { lat, lng, name, subtitle, zoom } = point;

  if (!GOOGLE_MAPS_KEY || _gmapsAuthFailed) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
          <div style={styles.modalNoKey}>
            <p style={{ fontSize: 14, color: '#666' }}>
              {_gmapsAuthFailed ? 'Google Maps failed to load.' : 'Google Maps API key not configured.'}
            </p>
            <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
              {_gmapsAuthFailed
                ? 'The API key may be invalid or billing may not be enabled on the Google Cloud project.'
                : <>Set <code>REACT_APP_GOOGLE_MAPS_KEY</code> in your environment.</>}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalFull} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>
            {name}{subtitle ? <span style={styles.modalSubtitle}> — {subtitle}</span> : null}
          </span>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={{ flex: 1 }}>
          <GoogleSatelliteMap lat={lat} lng={lng} zoom={zoom} interactive />
        </div>
      </div>
    </div>
  );
}

function SatelliteTile({ point, onClick }) {
  const { lat, lng, name, subtitle, zoom, color } = point;
  return (
    <div style={styles.gridTile} onClick={() => onClick(point)}>
      {GOOGLE_MAPS_KEY ? (
        <GoogleSatelliteMap lat={lat} lng={lng} zoom={zoom} />
      ) : (
        <div style={styles.tileNoKey}>No API key</div>
      )}
      <div style={styles.tileLabel}>
        <span style={{ ...styles.legendDot, background: color || MARKER_COLOR }} />
        <span style={styles.tileName}>{name}</span>
        {subtitle ? <span style={styles.tileSubtitle}>{subtitle}</span> : null}
      </div>
      <div style={styles.tileClickCatcher} />
    </div>
  );
}

// ========================================================================
// WORLD MAP — Canvas globe with rAF render loop
// ========================================================================
// ALL geometry (sphere, graticule, countries, markers) is drawn in a SINGLE
// requestAnimationFrame callback. Rotation and scale live in REFS, updated
// directly by pointer event handlers — zero React setState in the hot path.
// This guarantees every pixel reflects the same rotation/scale on every
// frame, eliminating the stuck-country / stagger / jitter bug.
//
// React state is ONLY used for:
// - tooltip (DOM overlay, outside canvas)
// - loading state (lib readiness)

function WorldMap({ points, onMarkerClick }) {
  const [tooltip, setTooltip] = useState(null);
  const [geoLib, setGeoLib] = useState(null);
  const [topoLib, setTopoLib] = useState(null);
  const [topoData, setTopoData] = useState(null);
  const [ready, setReady] = useState(false);

  // ── Refs: single source of truth for globe state ──
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rotationRef = useRef([...INITIAL_ROTATION]);
  const scaleRef = useRef(BASE_SCALE);
  const dirtyRef = useRef(true);
  const hoveredRef = useRef(-1);
  const featuresRef = useRef([]);
  const projectionRef = useRef(null);
  const pathRef = useRef(null);
  const graticuleRef = useRef(null);
  const geoLibRef = useRef(null);
  const dprRef = useRef(window.devicePixelRatio || 1);
  // Actual logical dimensions of the canvas (set by sizeCanvas)
  const sizeRef = useRef({ w: MAP_WIDTH, h: MAP_HEIGHT });

  // Drag state
  const dragging = useRef(false);
  const dragStart = useRef([0, 0]);
  const rotationStart = useRef([0, 0]);
  const clickStart = useRef([0, 0]);

  // Keep geoLib in a ref too so draw() can access it without closure staling
  useEffect(() => { geoLibRef.current = geoLib; }, [geoLib]);

  // Load d3-geo and topojson-client
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      import('d3-geo'),
      import('topojson-client')
    ]).then(([d3Geo, topoClient]) => {
      if (!cancelled) {
        setGeoLib(d3Geo);
        setTopoLib(topoClient);
      }
    }).catch(err => console.warn('Failed to load d3-geo / topojson-client:', err));
    return () => { cancelled = true; };
  }, []);

  // Load TopoJSON
  useEffect(() => {
    fetch(TOPO_URL).then(r => r.json()).then(setTopoData)
      .catch(err => console.warn('Failed to load TopoJSON:', err));
  }, []);

  // ── Initialize projection, features, graticule (once) ──
  useEffect(() => {
    if (!geoLib || !topoLib || !topoData) return;

    const objKey = topoData.objects.countries
      ? 'countries'
      : Object.keys(topoData.objects)[0];
    if (!objKey) return;

    const fc = topoLib.feature(topoData, topoData.objects[objKey]);
    featuresRef.current = (fc.features || []);

    projectionRef.current = geoLib.geoOrthographic()
      .scale(scaleRef.current)
      .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2])
      .rotate([-rotationRef.current[0], -rotationRef.current[1], 0])
      .clipAngle(90);

    pathRef.current = geoLib.geoPath(projectionRef.current);
    graticuleRef.current = geoLib.geoGraticule10();

    dirtyRef.current = true;
    setReady(true);
  }, [geoLib, topoLib, topoData]);

  // ── Canvas sizing: fit container, preserve aspect ratio ──
  const sizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const aspect = MAP_WIDTH / MAP_HEIGHT;

    // Fit within container preserving aspect ratio
    let w, h;
    if (cw / ch > aspect) {
      h = ch;
      w = ch * aspect;
    } else {
      w = cw;
      h = cw / aspect;
    }

    // Round to avoid sub-pixel issues
    w = Math.round(w);
    h = Math.round(h);

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    sizeRef.current = { w, h };

    // Update projection translate to center of actual canvas
    if (projectionRef.current) {
      projectionRef.current.translate([w / 2, h / 2]);
    }

    dirtyRef.current = true;
  }, []);

  useEffect(() => {
    if (!ready) return;
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);

    // ResizeObserver catches container changes without window resize
    let ro;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => sizeCanvas());
      ro.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', sizeCanvas);
      if (ro) ro.disconnect();
    };
  }, [ready, sizeCanvas]);

  // ── The draw function: ALL geometry in one pass ──
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const proj = projectionRef.current;
    const path = pathRef.current;
    const grat = graticuleRef.current;
    const features = featuresRef.current;
    const d3g = geoLibRef.current;
    if (!canvas || !proj || !path || !features.length || !d3g) return;

    const dpr = dprRef.current;
    const { w, h } = sizeRef.current;
    const ctx = canvas.getContext('2d');

    // Sync projection to current rotation/scale/size
    proj.scale(scaleRef.current);
    proj.rotate([-rotationRef.current[0], -rotationRef.current[1], 0]);
    proj.translate([w / 2, h / 2]);

    // Clear
    ctx.clearRect(0, 0, w * dpr, h * dpr);
    ctx.save();
    ctx.scale(dpr, dpr);

    // 1. Sphere outline
    ctx.beginPath();
    path.context(ctx)({ type: 'Sphere' });
    ctx.strokeStyle = SPHERE_STROKE;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // 2. Graticule
    ctx.beginPath();
    path(grat);
    ctx.strokeStyle = GRATICULE_STROKE;
    ctx.lineWidth = 0.3;
    ctx.stroke();

    // 3. Countries — single loop, same projection state
    const hovered = hoveredRef.current;
    for (let i = 0; i < features.length; i++) {
      ctx.beginPath();
      path(features[i]);
      ctx.fillStyle = (i === hovered) ? LAND_HOVER : LAND_COLOR;
      ctx.fill();
      ctx.strokeStyle = LAND_STROKE;
      ctx.lineWidth = 0.4;
      ctx.stroke();
    }

    // 4. Sphere outline on top (clean edge)
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.strokeStyle = SPHERE_STROKE;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // 5. Markers
    const center = proj.invert([w / 2, h / 2]);
    if (center) {
      for (const p of points) {
        const dist = d3g.geoDistance([p.lng, p.lat], center);
        if (dist > Math.PI / 2) continue;
        const pt = proj([p.lng, p.lat]);
        if (!pt) continue;
        ctx.beginPath();
        ctx.arc(pt[0], pt[1], 3.75, 0, 2 * Math.PI);
        ctx.fillStyle = p.color || MARKER_COLOR;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }

    ctx.restore();
  }, [points]);

  // Markers stream in as geocoding resolves — redraw when the set changes
  useEffect(() => { dirtyRef.current = true; }, [points]);

  // ── rAF loop ──
  useEffect(() => {
    if (!ready) return;
    let animId;
    const loop = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        draw();
      }
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [ready, draw]);

  // ── Sensitivity ──
  const getSensitivity = useCallback(() => 0.4 * (BASE_SCALE / scaleRef.current), []);

  // ── Mark dirty ──
  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);

  // ── Zoom ──
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.92 : 1.09;
    scaleRef.current = Math.min(MAX_ZOOM * BASE_SCALE, Math.max(MIN_ZOOM * BASE_SCALE, scaleRef.current * factor));
    markDirty();
  }, [markDirty]);

  // ── Rotation (drag) ──
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    clickStart.current = [e.clientX, e.clientY];
    dragging.current = true;
    dragStart.current = [e.clientX, e.clientY];
    rotationStart.current = [...rotationRef.current];
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (dragging.current) {
      const s = getSensitivity();
      const dx = e.clientX - dragStart.current[0];
      const dy = e.clientY - dragStart.current[1];
      rotationRef.current = [
        rotationStart.current[0] - dx * s,
        Math.max(-80, Math.min(80, rotationStart.current[1] + dy * s))
      ];
      markDirty();
      return; // skip hit-testing while dragging
    }

    // Hit-test for hover (only when idle)
    const proj = projectionRef.current;
    const d3g = geoLibRef.current;
    if (!proj || !d3g || !featuresRef.current.length) return;

    // Sync projection before hit-testing
    proj.scale(scaleRef.current);
    proj.rotate([-rotationRef.current[0], -rotationRef.current[1], 0]);
    const { w: sw, h: sh } = sizeRef.current;
    proj.translate([sw / 2, sh / 2]);

    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * sw;
    const y = ((e.clientY - rect.top) / rect.height) * sh;

    // Check markers first
    const center = proj.invert([sw / 2, sh / 2]);
    if (center) {
      for (const p of points) {
        const dist = d3g.geoDistance([p.lng, p.lat], center);
        if (dist > Math.PI / 2) continue;
        const pt = proj([p.lng, p.lat]);
        if (!pt) continue;
        const mdx = x - pt[0];
        const mdy = y - pt[1];
        if (mdx * mdx + mdy * mdy < 49) {
          canvas.style.cursor = 'pointer';
          setTooltip({ name: p.name, subtitle: p.subtitle, x: e.clientX, y: e.clientY - 12 });
          return;
        }
      }
    }

    setTooltip(null);
    canvas.style.cursor = 'grab';

    // Country hover
    const coords = proj.invert([x, y]);
    if (coords) {
      let found = -1;
      for (let i = 0; i < featuresRef.current.length; i++) {
        if (d3g.geoContains(featuresRef.current[i], coords)) {
          found = i;
          break;
        }
      }
      if (found !== hoveredRef.current) {
        hoveredRef.current = found;
        markDirty();
      }
    }
  }, [getSensitivity, markDirty, points]);

  const handleMouseUp = useCallback((e) => {
    if (!dragging.current) return;
    dragging.current = false;

    // Click-to-rotate if mouse barely moved
    const dx = e.clientX - clickStart.current[0];
    const dy = e.clientY - clickStart.current[1];
    if (Math.abs(dx) < 4 && Math.abs(dy) < 4) {
      const canvas = canvasRef.current;
      const proj = projectionRef.current;
      const d3g = geoLibRef.current;
      if (!canvas || !proj || !d3g) return;

      proj.scale(scaleRef.current);
      proj.rotate([-rotationRef.current[0], -rotationRef.current[1], 0]);
      const { w: sw, h: sh } = sizeRef.current;
      proj.translate([sw / 2, sh / 2]);

      const rect = canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * sw;
      const y = ((e.clientY - rect.top) / rect.height) * sh;
      const coords = proj.invert([x, y]);
      if (!coords) return;

      // Check if clicked a marker
      const center = proj.invert([sw / 2, sh / 2]);
      if (center) {
        for (const p of points) {
          const dist = d3g.geoDistance([p.lng, p.lat], center);
          if (dist > Math.PI / 2) continue;
          const pt = proj([p.lng, p.lat]);
          if (!pt) continue;
          const mdx = x - pt[0];
          const mdy = y - pt[1];
          if (mdx * mdx + mdy * mdy < 49) {
            onMarkerClick(p);
            return;
          }
        }
      }

      // Rotate to clicked point
      rotationRef.current = [coords[0], coords[1]];
      markDirty();
    }
  }, [markDirty, points, onMarkerClick]);

  const handleMouseLeave = useCallback(() => {
    dragging.current = false;
    if (hoveredRef.current !== -1) {
      hoveredRef.current = -1;
      markDirty();
    }
    setTooltip(null);
  }, [markDirty]);

  // ── Touch ──
  const lastTouchDist = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      dragging.current = true;
      dragStart.current = [e.touches[0].clientX, e.touches[0].clientY];
      rotationStart.current = [...rotationRef.current];
    } else if (e.touches.length === 2) {
      const tdx = e.touches[0].clientX - e.touches[1].clientX;
      const tdy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(tdx * tdx + tdy * tdy);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging.current) {
      const s = getSensitivity();
      const dx = e.touches[0].clientX - dragStart.current[0];
      const dy = e.touches[0].clientY - dragStart.current[1];
      rotationRef.current = [
        rotationStart.current[0] - dx * s,
        Math.max(-80, Math.min(80, rotationStart.current[1] + dy * s))
      ];
      markDirty();
    } else if (e.touches.length === 2 && lastTouchDist.current) {
      const tdx = e.touches[0].clientX - e.touches[1].clientX;
      const tdy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(tdx * tdx + tdy * tdy);
      const pinchScale = dist / lastTouchDist.current;
      scaleRef.current = Math.min(MAX_ZOOM * BASE_SCALE, Math.max(MIN_ZOOM * BASE_SCALE, scaleRef.current * pinchScale));
      lastTouchDist.current = dist;
      markDirty();
    }
  }, [getSensitivity, markDirty]);

  const handleTouchEnd = useCallback(() => {
    dragging.current = false;
    lastTouchDist.current = null;
  }, []);

  // ── Non-passive listeners ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !ready) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchmove', handleTouchMove);
    };
  }, [ready, handleWheel, handleTouchMove]);

  // ── Reset ──
  const handleReset = useCallback(() => {
    rotationRef.current = [...INITIAL_ROTATION];
    scaleRef.current = BASE_SCALE;
    hoveredRef.current = -1;
    setTooltip(null);
    markDirty();
  }, [markDirty]);

  // ── Loading ──
  if (!ready) {
    return (
      <div style={styles.mapContainer}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <span style={{ fontSize: 12, color: '#999' }}>Loading globe…</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mapContainer} ref={containerRef}>
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          cursor: 'grab',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />

      {tooltip && (
        <div style={{
          ...styles.tooltip,
          left: tooltip.x,
          top: tooltip.y,
          transform: 'translate(-50%, -100%)'
        }}>
          {tooltip.name}
          {tooltip.subtitle ? <span style={styles.tooltipSubtitle}>{tooltip.subtitle}</span> : null}
        </div>
      )}

      <button
        onClick={handleReset}
        title="Reset globe"
        style={styles.resetBtn}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
      </button>
    </div>
  );
}

// ========================================================================
// MAIN PAGE COMPONENT
// ========================================================================
export default function ProjectMapPage({
  useSupabaseDB,
  activeProjects,
  navHeight = 73,
  canViewInquiries = false,
  canViewTeam = false,
}) {
  const [viewMode, setViewMode] = useState('map');
  // Every layer toggles independently, so the map can show any combination —
  // including team members on their own, with projects switched off.
  const [showProjects, setShowProjects] = useState(true);
  const [showInquiries, setShowInquiries] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [teamScope, setTeamScope] = useState(TEAM_SCOPE_ACTIVE);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const [projects, setProjects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [inquiriesLoaded, setInquiriesLoaded] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  // A layer only shows if the role allows it, even when its toggle was
  // flipped on before the role changed.
  const projectsVisible = showProjects;
  const inquiriesVisible = canViewInquiries && showInquiries;
  const teamVisible = canViewTeam && showTeam;

  // ── Projects ──
  useEffect(() => {
    async function loadProjects() {
      if (useSupabaseDB) {
        const hasAddresses = activeProjects?.some(p => p.projectAddress || p.project_address);
        if (hasAddresses) {
          setProjects(activeProjects.map(p => ({
            id: p.id, name: p.name,
            address: p.projectAddress || p.project_address || ''
          })));
          return;
        }
        try {
          const { data, error } = await supabase
            .from('projects')
            .select('id, name, project_address');
          if (error) throw error;
          setProjects((data || []).map(p => ({
            id: p.id, name: p.name, address: p.project_address || ''
          })));
        } catch (err) {
          console.warn('Failed to fetch project addresses:', err);
          setProjects((activeProjects || []).map(p => ({
            id: p.id, name: p.name,
            address: p.projectAddress || p.project_address || ''
          })));
        }
      } else {
        setProjects((activeProjects || []).map(p => ({
          id: p.id, name: p.name,
          address: p.projectAddress || p.project_address || ''
        })));
      }
    }
    loadProjects();
  }, [useSupabaseDB, activeProjects]);

  // ── Inquiries (loaded the first time the layer is switched on) ──
  useEffect(() => {
    if (!inquiriesVisible || inquiriesLoaded || !useSupabaseDB) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('inquiries')
          .select('id, project_name, client_name_override, location_city, location_coordinates')
          .eq('archived', false);
        if (error) throw error;
        if (cancelled) return;
        setInquiries((data || []).map(i => ({
          id: i.id,
          name: i.project_name || i.client_name_override || 'Untitled inquiry',
          city: (i.location_city || '').trim(),
          coordinates: parseCoordinates(i.location_coordinates),
        })));
        setInquiriesLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to fetch inquiries:', err);
        setLoadError('Could not load inquiry locations.');
      }
    })();

    return () => { cancelled = true; };
  }, [inquiriesVisible, inquiriesLoaded, useSupabaseDB]);

  // ── Team members (loaded on first enable; scope filtering is client-side) ──
  useEffect(() => {
    if (!teamVisible || teamLoaded || !useSupabaseDB) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const [usersRes, profilesRes] = await Promise.all([
          supabase.from('users').select('id, name, is_active, job_role').is('deleted_at', null),
          supabase.from('user_profiles').select('user_id, home_city'),
        ]);
        if (usersRes.error) throw usersRes.error;
        if (profilesRes.error) throw profilesRes.error;
        if (cancelled) return;

        const homeCityByUser = new Map();
        (profilesRes.data || []).forEach(p => {
          homeCityByUser.set(String(p.user_id), (p.home_city || '').trim());
        });

        setTeamMembers((usersRes.data || []).map(u => ({
          id: u.id,
          name: u.name || 'Unnamed',
          jobRole: u.job_role || '',
          // `is_active !== false` is the app-wide "active employee" rule
          isActive: u.is_active !== false,
          homeCity: homeCityByUser.get(String(u.id)) || '',
        })));
        setTeamLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to fetch team home towns:', err);
        setLoadError('Could not load team home towns.');
      }
    })();

    return () => { cancelled = true; };
  }, [teamVisible, teamLoaded, useSupabaseDB]);

  // ── Points per layer ──
  // project_address is in practice a coordinate pair (DMS or decimal) rather
  // than a street address, so parse it and skip geocoding entirely. Anything
  // that isn't coordinates falls back to a geocoded lookup, cached by the
  // address text like every other layer.
  const projectPoints = useMemo(() => projects
    .filter(p => p.address && p.address.trim())
    .map(p => {
      const coords = parseCoordinates(p.address);
      return {
        key: `project-${p.id}`,
        cacheKey: coords ? null : normalizeQuery(p.address),
        kind: 'project',
        name: p.name,
        subtitle: '',
        address: coords ? '' : p.address,
        lat: coords?.lat,
        lng: coords?.lng,
        zoom: coords ? ZOOM_COORDINATES : ZOOM_ADDRESS,
        color: MARKER_COLOR,
      };
    }), [projects]);

  const inquiryPoints = useMemo(() => inquiries
    .filter(i => i.coordinates || i.city)
    .map(i => ({
      key: `inquiry-${i.id}`,
      // Coordinates come straight from the record; city lookups are shared
      // across every inquiry naming the same place.
      cacheKey: i.coordinates ? null : normalizeQuery(i.city),
      kind: 'inquiry',
      name: i.name,
      subtitle: i.city || 'Inquiry',
      address: i.coordinates ? '' : i.city,
      lat: i.coordinates?.lat,
      lng: i.coordinates?.lng,
      zoom: i.coordinates ? ZOOM_COORDINATES : ZOOM_CITY,
      color: INQUIRY_MARKER_COLOR,
    })), [inquiries]);

  const scopedTeamMembers = useMemo(() => teamMembers.filter(m => {
    if (teamScope === TEAM_SCOPE_ACTIVE) return m.isActive;
    if (teamScope === TEAM_SCOPE_INACTIVE) return !m.isActive;
    return true;
  }), [teamMembers, teamScope]);

  const teamPoints = useMemo(() => scopedTeamMembers
    .filter(m => m.homeCity)
    .map(m => ({
      key: `user-${m.id}`,
      cacheKey: normalizeQuery(m.homeCity),
      kind: 'team',
      name: m.name,
      subtitle: m.homeCity,
      address: m.homeCity,
      zoom: ZOOM_HOME_TOWN,
      color: TEAM_MARKER_COLOR,
    })), [scopedTeamMembers]);

  const textCache = useSupabaseDB ? locationGeocodeCache : null;

  const sites = useGeocodedPoints(projectPoints, {
    enabled: projectsVisible,
    cache: textCache,
  });
  const inquiryLayer = useGeocodedPoints(inquiryPoints, {
    enabled: inquiriesVisible,
    cache: textCache,
  });
  const team = useGeocodedPoints(teamPoints, {
    enabled: teamVisible,
    cache: textCache,
  });

  const visiblePoints = useMemo(
    () => [...sites.points, ...inquiryLayer.points, ...team.points],
    [sites.points, inquiryLayer.points, team.points]
  );

  const displayPoints = useMemo(() => {
    if (viewMode === 'grid' && !showAll && visiblePoints.length > 12)
      return visiblePoints.slice(0, 12);
    return visiblePoints;
  }, [visiblePoints, viewMode, showAll]);

  // Collapse the grid again whenever the visible set changes
  useEffect(() => { setShowAll(false); }, [projectsVisible, inquiriesVisible, teamVisible, teamScope]);

  const handleMarkerClick = useCallback((point) => {
    setSelectedPoint(point);
  }, []);

  const geocoding = sites.geocoding || inquiryLayer.geocoding || team.geocoding;

  // ── Notes about records that can't be plotted ──
  const missingAddresses = projects.filter(p => !p.address || !p.address.trim()).length;
  const missingInquiryLocations = inquiries.filter(i => !i.coordinates && !i.city).length;
  const missingHomeTowns = scopedTeamMembers.filter(m => !m.homeCity).length;

  const legend = [
    ...(projectsVisible
      ? [{ key: 'projects', label: 'Projects', color: MARKER_COLOR, count: sites.points.length }]
      : []),
    ...(inquiriesVisible
      ? [{ key: 'inquiries', label: 'Inquiries', color: INQUIRY_MARKER_COLOR, count: inquiryLayer.points.length }]
      : []),
    ...(teamVisible
      ? [{ key: 'team', label: `Team members — ${TEAM_SCOPE_LABELS[teamScope].toLowerCase()}`, color: TEAM_MARKER_COLOR, count: team.points.length }]
      : []),
  ];

  const renderProgress = (label, state) => (state.geocoding ? (
    <div style={styles.progressBar} key={label}>
      <div style={styles.progressText}>
        Geocoding {label}… {state.progress.done} / {state.progress.total}
      </div>
      <div style={styles.progressTrack}>
        <div style={{
          ...styles.progressFill,
          width: `${state.progress.total ? (state.progress.done / state.progress.total) * 100 : 0}%`
        }} />
      </div>
    </div>
  ) : null);

  return (
    <div style={{ ...styles.page, height: `calc(100vh - ${navHeight}px)` }}>
      <div style={styles.header}>
        <div style={styles.headerRow}>
          <div style={styles.layerControls}>
            <button
              style={{ ...styles.pillBtn, ...(showProjects ? styles.pillActive : {}) }}
              onClick={() => setShowProjects(v => !v)}
              aria-pressed={showProjects}
              title="Show project locations"
            >
              <span style={{ ...styles.legendDot, background: MARKER_COLOR }} />
              Projects
            </button>

            {canViewInquiries && (
              <button
                style={{ ...styles.pillBtn, ...(showInquiries ? styles.pillActive : {}) }}
                onClick={() => setShowInquiries(v => !v)}
                aria-pressed={showInquiries}
                title="Show inquiry locations"
              >
                <span style={{ ...styles.legendDot, background: INQUIRY_MARKER_COLOR }} />
                Inquiries
              </button>
            )}

            {canViewTeam && (
              <div style={styles.teamControls}>
                <button
                  style={{ ...styles.pillBtn, ...(showTeam ? styles.pillActive : {}) }}
                  onClick={() => setShowTeam(v => !v)}
                  aria-pressed={showTeam}
                  title="Show team members' home towns"
                >
                  <span style={{ ...styles.legendDot, background: TEAM_MARKER_COLOR }} />
                  Team Member
                </button>
                {showTeam && (
                  <div style={styles.toggleGroup}>
                    {[TEAM_SCOPE_ACTIVE, TEAM_SCOPE_INACTIVE, TEAM_SCOPE_ALL].map(scope => (
                      <button
                        key={scope}
                        style={{ ...styles.toggleBtn, ...(teamScope === scope ? styles.toggleActive : {}) }}
                        onClick={() => setTeamScope(scope)}
                      >{TEAM_SCOPE_LABELS[scope]}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.toggleGroup}>
            <button
              style={{ ...styles.toggleBtn, ...(viewMode === 'map' ? styles.toggleActive : {}) }}
              onClick={() => setViewMode('map')}
            >Map View</button>
            <button
              style={{ ...styles.toggleBtn, ...(viewMode === 'grid' ? styles.toggleActive : {}) }}
              onClick={() => setViewMode('grid')}
            >Grid View</button>
          </div>
        </div>

        <div style={styles.legendRow}>
          {legend.map(item => (
            <span key={item.key} style={styles.legendItem}>
              <span style={{ ...styles.legendDot, background: item.color }} />
              {item.label} ({item.count})
            </span>
          ))}
        </div>

        {projectsVisible && renderProgress('projects', sites)}
        {inquiriesVisible && renderProgress('inquiries', inquiryLayer)}
        {teamVisible && renderProgress('home towns', team)}

        {loadError && <div style={styles.errorNote}>{loadError}</div>}

        {projectsVisible && missingAddresses > 0 && !sites.geocoding && (
          <div style={styles.infoNote}>
            {missingAddresses} project{missingAddresses > 1 ? 's' : ''} without an address — not shown on the map.
          </div>
        )}

        {inquiriesVisible && missingInquiryLocations > 0 && !inquiryLayer.geocoding && (
          <div style={styles.infoNote}>
            {missingInquiryLocations} inquir{missingInquiryLocations > 1 ? 'ies' : 'y'} without a location — not shown on the map.
          </div>
        )}

        {teamVisible && missingHomeTowns > 0 && !team.geocoding && (
          <div style={styles.infoNote}>
            {missingHomeTowns} team member{missingHomeTowns > 1 ? 's' : ''} without a home town — not shown on the map.
          </div>
        )}
      </div>

      <div style={styles.content}>
        {viewMode === 'map' ? (
          <WorldMap points={visiblePoints} onMarkerClick={handleMarkerClick} />
        ) : (
          <>
            {visiblePoints.length === 0 && !geocoding ? (
              <div style={styles.emptyState}>
                <p style={{ fontSize: 14, color: '#666' }}>No geocoded locations to display.</p>
              </div>
            ) : (
              <>
                <div style={styles.grid}>
                  {displayPoints.map(p => (
                    <SatelliteTile key={p.key} point={p} onClick={handleMarkerClick} />
                  ))}
                </div>
                {!showAll && visiblePoints.length > 12 && (
                  <div style={styles.showAllRow}>
                    <button style={styles.showAllBtn} onClick={() => setShowAll(true)}>
                      Show All ({visiblePoints.length} locations)
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <SatelliteModal point={selectedPoint} onClose={() => setSelectedPoint(null)} />
    </div>
  );
}

// ========================================================================
// STYLES
// ========================================================================
/* @audit cat="01" issue="Styles object uses many hardcoded colors (#666, #999, #888, #333, #ddd, #ccc, #eee, #fafafa) not in BRIO palette" fix="Replace with var(--brio-*) tokens" */
const styles = {
  page: {
    padding: '0 24px',
    maxWidth: 1400,
    margin: '0 auto',
    fontFamily: 'inherit',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: { paddingTop: 20, marginBottom: 8, flexShrink: 0, maxWidth: 1400, width: '100%', margin: '0 auto 8px' },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap'
  },
  layerControls: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'
  },
  teamControls: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
  },
  pillBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', fontSize: 12, fontWeight: 400,
    border: '1px solid #ddd', borderRadius: 6,
    background: '#fafafa', color: '#666',
    cursor: 'pointer', transition: 'all 0.15s ease',
    letterSpacing: '0.03em'
  },
  pillActive: { background: '#111', color: '#fff', borderColor: '#111', fontWeight: 500 },
  legendRow: {
    display: 'flex', alignItems: 'center', gap: 16,
    flexWrap: 'wrap', marginBottom: 6
  },
  legendItem: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 11, color: '#666', letterSpacing: '0.02em'
  },
  legendDot: {
    display: 'inline-block', width: 8, height: 8,
    borderRadius: '50%', flexShrink: 0
  },
  pageTitle: {
    fontSize: 20, fontWeight: 400, // AUDIT: fontSize 20 not in BRIO scale (use 16 or 24)
    letterSpacing: '0.02em', color: '#111', margin: 0
  },
  toggleGroup: {
    display: 'flex',
    border: '1px solid #ddd',
    borderRadius: 6,
    overflow: 'hidden'
  },
  toggleBtn: {
    padding: '6px 16px', fontSize: 12, fontWeight: 400,
    border: 'none', background: '#fafafa', color: '#666',
    cursor: 'pointer', transition: 'all 0.15s ease',
    letterSpacing: '0.03em'
  },
  toggleActive: { background: '#111', color: '#fff', fontWeight: 500 },
  progressBar: { marginBottom: 8 },
  progressText: { fontSize: 11, color: '#888', marginBottom: 4 },
  progressTrack: { height: 3, background: '#eee', borderRadius: 2, overflow: 'hidden' },
  progressFill: {
    height: '100%', background: BRIO_GOLD_HEX,
    borderRadius: 2, transition: 'width 0.3s ease'
  },
  infoNote: { fontSize: 11, color: '#999', fontStyle: 'italic' },
  errorNote: { fontSize: 11, color: '#b3261e' },
  content: { flex: 1, minHeight: 0, overflow: 'hidden' },
  emptyState: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 300, background: '#f9f9f9', border: '1px solid #eee'
  },
  mapContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  tooltip: {
    position: 'fixed', zIndex: 1000,
    background: '#111', color: '#fff',
    padding: '4px 10px', borderRadius: 4,
    fontSize: 11, fontWeight: 400,
    pointerEvents: 'none', whiteSpace: 'nowrap',
    letterSpacing: '0.02em'
  },
  tooltipSubtitle: { color: '#bbb', marginLeft: 6 },
  resetBtn: {
    position: 'absolute',
    bottom: 12, right: 12,
    width: 28, height: 28,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.85)',
    border: '1px solid #ddd',
    borderRadius: 4,
    cursor: 'pointer',
    color: '#666',
    padding: 0,
    zIndex: 10
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16
  },
  gridTile: {
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    aspectRatio: '16/10',
    background: 'none',
    borderRadius: 0,
    border: 'none'
  },
  tileNoKey: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100%', fontSize: 12, color: '#999'
  },
  tileLabel: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    padding: '6px 8px',
    background: '#fff',
    display: 'flex', alignItems: 'center', gap: 6,
    pointerEvents: 'none', zIndex: 0
  },
  tileName: {
    color: '#111', fontSize: 11, fontWeight: 500,
    letterSpacing: '0.02em'
  },
  tileSubtitle: {
    color: '#999', fontSize: 10, fontWeight: 400,
    letterSpacing: '0.02em', marginLeft: 'auto',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  tileClickCatcher: {
    position: 'absolute', inset: 0, zIndex: 1, cursor: 'pointer'
  },
  showAllRow: { display: 'flex', justifyContent: 'center', marginTop: 16 },
  showAllBtn: {
    padding: '8px 20px', fontSize: 12, fontWeight: 400,
    border: '1px solid #ccc', borderRadius: 6,
    background: '#fff', cursor: 'pointer', color: '#333',
    letterSpacing: '0.02em'
  },
  modalOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.8)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  modalFull: {
    width: '95vw', height: '92vh',
    background: '#000', borderRadius: 0,
    overflow: 'hidden', display: 'flex', flexDirection: 'column'
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 16px', background: '#111'
  },
  modalTitle: {
    color: '#fff', fontSize: 13, fontWeight: 400,
    letterSpacing: '0.03em'
  },
  modalSubtitle: { color: '#999' },
  modalClose: {
    background: 'none', border: 'none', color: '#aaa',
    fontSize: 18, cursor: 'pointer', padding: '4px 8px', lineHeight: 1
  },
  modalContent: {
    width: 400, background: '#fff', padding: 32, textAlign: 'center'
  },
  modalNoKey: { padding: 16 }
};
